import React, { useState } from 'react';
import { Modal, Form, Select, Checkbox, Input, Button, message, Space, Alert } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { exportCSV, exportExcel, downloadFile, type ExportRequest } from '../api/export';

const { Option } = Select;

interface ExportDialogProps {
  visible: boolean;
  onClose: () => void;
  dataType: 'price' | 'storage' | 'production' | 'trade';
  availableFields: { value: string; label: string }[];
  currentFilters?: Record<string, any>;
}

/**
 * 数据导出对话框组件
 * 
 * 功能：
 * - 选择导出格式（CSV/Excel）
 * - 选择导出字段
 * - 显示当前筛选条件
 * - 处理小文件直接下载
 * - 处理大文件邮件发送
 * 
 * 验证需求：10.1, 10.2, 10.4
 */
const ExportDialog: React.FC<ExportDialogProps> = ({
  visible,
  onClose,
  dataType,
  availableFields,
  currentFilters,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // 数据类型名称映射
  const dataTypeNames: Record<string, string> = {
    price: '价格数据',
    storage: '储能项目',
    production: '产量数据',
    trade: '贸易路径',
  };

  // 处理导出
  const handleExport = async (values: any) => {
    setLoading(true);
    try {
      const request: ExportRequest = {
        data_type: dataType,
        format: values.format,
        fields: selectedFields.length > 0 ? selectedFields : undefined,
        filters: currentFilters,
        email: values.email,
      };

      // 根据格式调用不同的API
      const blob = values.format === 'csv' 
        ? await exportCSV(request)
        : await exportExcel(request);

      // 生成文件名
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `${dataTypeNames[dataType]}_${timestamp}.${values.format}`;

      // 触发下载
      downloadFile(blob, filename);

      message.success('导出成功！');
      onClose();
      form.resetFields();
      setSelectedFields([]);
    } catch (error: any) {
      console.error('导出失败:', error);
      
      // 如果是大文件，显示邮件发送提示
      if (error.response?.status === 413 || error.response?.data?.message?.includes('邮件')) {
        message.info('数据量较大，下载链接将发送到您的邮箱');
      } else {
        message.error('导出失败：' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedFields(availableFields.map(f => f.value));
    } else {
      setSelectedFields([]);
    }
  };

  return (
    <Modal
      title={`导出${dataTypeNames[dataType]}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleExport}
        initialValues={{
          format: 'csv',
        }}
      >
        {/* 导出格式 */}
        <Form.Item
          label="导出格式"
          name="format"
          rules={[{ required: true, message: '请选择导出格式' }]}
        >
          <Select>
            <Option value="csv">CSV</Option>
            <Option value="excel">Excel</Option>
          </Select>
        </Form.Item>

        {/* 字段选择 */}
        <Form.Item label="选择字段">
          <div style={{ marginBottom: '8px' }}>
            <Checkbox
              checked={selectedFields.length === availableFields.length}
              indeterminate={selectedFields.length > 0 && selectedFields.length < availableFields.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              全选
            </Checkbox>
          </div>
          <Checkbox.Group
            value={selectedFields}
            onChange={(values) => setSelectedFields(values as string[])}
            style={{ width: '100%' }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {availableFields.map(field => (
                <Checkbox key={field.value} value={field.value}>
                  {field.label}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
            {selectedFields.length === 0 ? '未选择字段时将导出所有字段' : `已选择 ${selectedFields.length} 个字段`}
          </div>
        </Form.Item>

        {/* 当前筛选条件 */}
        {currentFilters && Object.keys(currentFilters).length > 0 && (
          <Alert
            message="当前筛选条件"
            description={
              <div style={{ fontSize: '12px' }}>
                {Object.entries(currentFilters).map(([key, value]) => (
                  <div key={key}>
                    <strong>{key}:</strong> {JSON.stringify(value)}
                  </div>
                ))}
              </div>
            }
            type="info"
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* 邮箱（大文件时使用） */}
        <Form.Item
          label="邮箱地址（可选）"
          name="email"
          extra="数据量超过50000条时，下载链接将发送到此邮箱"
        >
          <Input placeholder="your@email.com" type="email" />
        </Form.Item>

        {/* 提交按钮 */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<DownloadOutlined />}
            >
              导出
            </Button>
            <Button onClick={onClose}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ExportDialog;
